'use client';

import { useEffect } from 'react';

interface Section {
  id: string;
  offset: number;
}

interface SectionAnchorManagerProps {
  sections: Section[];
  scrollBehavior?: ScrollBehavior;
  offsetAdjustment?: number;
}

const SectionAnchorManager = ({ 
  sections, 
  scrollBehavior = 'smooth',
  offsetAdjustment = 80 
}: SectionAnchorManagerProps) => {
  
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.substring(1);
      if (hash) {
        const section = sections.find(s => s.id === hash);
        if (section) {
          const element = document.getElementById(section.id);
          if (element) {
            const elementPosition = element.offsetTop - offsetAdjustment;
            window.scrollTo({
              top: elementPosition,
              behavior: scrollBehavior
            });
          }
        }
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    
    if (window.location.hash) {
      setTimeout(handleHashChange, 100);
    }

    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [sections, scrollBehavior, offsetAdjustment]);

  useEffect(() => {
    const updateHash = () => {
      const scrollPosition = window.scrollY + 150;
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i].id);
        if (section && section.offsetTop <= scrollPosition) {
          const newHash = `#${sections[i].id}`;
          if (window.location.hash !== newHash) {
            window.history.replaceState(null, '', newHash);
          }
          break;
        }
      }
    };

    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateHash();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  return null;
};

export default SectionAnchorManager;